/**
 * Customer Journey Module
 * --------------------------------------------------
 * Handles:
 *
 * 1. Collection → Product journey detection
 * 2. One-time sessionStorage recommendation
 * 3. PDP recommended plant type
 * 4. Recommended card positioning
 * 5. Plant variant drawer
 *
 * Dependencies:
 * - variant metafield custom.filter_form
 * - HTML data-group-handles
 * - HTML data-filter-handle
 */

(function () {
  'use strict';

  const STORAGE = {
    plantType: 'preferred_plant_filter_handle',
    collection: 'preferred_plant_collection',
    pending: 'plant_journey_pending'
  };


  /* ==================================================
     UTILITIES
  ================================================== */

  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[_–—/]+/g, ' ')
      .replace(/-/g, ' ')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }


  function normaliseHandle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '-');
  }


  /* ==================================================
     COLLECTION TYPE DETECTION
  ================================================== */

  function detectPlantType(collectionHandle, collectionTitle) {

    const collectionText = normalise(
      collectionHandle + ' ' + collectionTitle
    );

    const words = collectionText
      .split(' ')
      .filter(Boolean);


    function hasWord(word) {
      return words.includes(word);
    }


    function hasAnyWord(wordList) {
      return wordList.some(function (word) {
        return hasWord(word);
      });
    }


    function containsAnyPhrase(phrases) {
      return phrases.some(function (phrase) {
        return collectionText.includes(
          normalise(phrase)
        );
      });
    }


    /* Specialist */

    if (
      containsAnyPhrase([
        'top grafted',
        'top worked',
        'topwork',
        'lollipop tree',
        'lollipop trees',
        'patio tree',
        'patio trees'
      ])
    ) {
      return 'top-grafted-lollipop';
    }


    if (hasAnyWord(['pleached', 'pleach'])) {
      return 'pleached-tree';
    }


    if (hasWord('topiary')) {
      return 'topiary';
    }


    if (hasAnyWord(['trained', 'training'])) {
      return 'trained';
    }


    /*
    * Mature multi-stem
    *
    * Must be checked before generic multi-stem and mature.
    */
    if (
      containsAnyPhrase([
        'mature multi stem',
        'mature multi stems',
        'mature multistem',
        'mature multiple stem',
        'mature multiple stems'
      ])
    ) {
      return 'mature-multi-stem';
    }


    /* Multi stem */

    if (
      containsAnyPhrase([
        'multi stem',
        'multi stems',
        'multistem',
        'multiple stem',
        'multiple stems'
      ])
    ) {
      return 'multi-stem-tree';
    }


    /* Mature */

    if (hasWord('mature')) {
      return 'mature-tree';
    }


    /* Young / intermediate */

    if (
      hasWord('young') ||
      hasWord('intermediate') ||
      containsAnyPhrase([
        'single stem',
        'single stems',
        'single trunk'
      ])
    ) {
      return 'single-stem';
    }


    /*
    * Instant screening
    *
    * Must come BEFORE the generic screening rule.
    */
    if (
      containsAnyPhrase([
        'instant screening',
        'instant hedging',
        'instant hedge',
        'instant hedges'
      ])
    ) {
      return 'instant-hedging';
    }


    /* Screening */

    if (
      hasAnyWord([
        'screening',
        'hedging',
        'hedge',
        'hedges'
      ])
    ) {
      return 'hedging';
    }


    /* Climbers */

    if (
      hasAnyWord([
        'climber',
        'climbers',
        'climbing'
      ])
    ) {
      return 'climber';
    }


    /* Shrubs */

    if (
      hasAnyWord([
        'shrub',
        'shrubs',
        'bush',
        'bushes'
      ])
    ) {
      return 'bush';
    }


    /* Clumps */

    if (
      hasAnyWord([
        'clump',
        'clumps',
        'clumping'
      ])
    ) {
      return 'clump';
    }


    /* Bare root */

    if (
      containsAnyPhrase([
        'bare root',
        'bare roots',
        'bareroot',
        'bareroots'
      ])
    ) {
      return 'bare-root';
    }


    return '';
  }


  /* ==================================================
     COLLECTION → PRODUCT JOURNEY
  ================================================== */

  function initialiseJourneyTracking() {

    const context = document.querySelector(
      '[data-customer-journey-context]'
    );

    if (!context) return;


    const pageType =
      context.dataset.pageType || '';

    const collectionHandle =
      context.dataset.collectionHandle || '';

    const collectionTitle =
      context.dataset.collectionTitle || '';


    const isCollectionPage =
      pageType === 'collection';


    const collectionPlantType =
      isCollectionPage
        ? detectPlantType(
            collectionHandle,
            collectionTitle
          )
        : '';


    document.addEventListener(
      'click',
      function (event) {

        const link =
          event.target.closest('a[href]');

        if (!link) return;


        let url;

        try {

          url = new URL(
            link.href,
            window.location.origin
          );

        } catch (error) {

          return;
        }


        if (
          url.origin !==
          window.location.origin
        ) {
          return;
        }


        const isProductLink =
          url.pathname.indexOf(
            '/products/'
          ) !== -1;


        if (!isProductLink) {
          return;
        }


        /*
         * Relevant Collection → Product
         */

        if (
          isCollectionPage &&
          collectionPlantType
        ) {

          sessionStorage.setItem(
            STORAGE.plantType,
            collectionPlantType
          );

          sessionStorage.setItem(
            STORAGE.collection,
            collectionHandle
          );

          sessionStorage.setItem(
            STORAGE.pending,
            '1'
          );

          return;
        }


        /*
         * Product accessed from anywhere else.
         */

        clearJourney();

      }
    );
  }


  function clearJourney() {

    sessionStorage.removeItem(
      STORAGE.pending
    );

    sessionStorage.removeItem(
      STORAGE.plantType
    );

    sessionStorage.removeItem(
      STORAGE.collection
    );

  }


  /* ==================================================
     PDP RECOMMENDATION
  ================================================== */

  function initialiseRecommendation() {

    const sections =
      document.querySelectorAll(
        '[data-customer-journey-product]'
      );


    sections.forEach(function (section) {

      const grid =
        section.querySelector(
          '.selector-grid'
        );


      const cards =
        Array.from(
          section.querySelectorAll(
            '.js-plant-drawer-toggle'
          )
        );


      if (!grid || !cards.length) {

        sessionStorage.removeItem(
          STORAGE.pending
        );

        return;
      }


      const params =
        new URLSearchParams(
          window.location.search
        );


      /*
       * URL has highest priority.
       */

      const urlPlantType =
        normaliseHandle(
          params.get('plant_type') ||
          params.get('filter_form')
        );


      const journeyPending =
        sessionStorage.getItem(
          STORAGE.pending
        ) === '1';


      const storedPlantType =
        journeyPending
          ? normaliseHandle(
              sessionStorage.getItem(
                STORAGE.plantType
              )
            )
          : '';


      const preferredPlantType =
        urlPlantType ||
        storedPlantType;


      /*
       * Consume one-time journey.
       */

      sessionStorage.removeItem(
        STORAGE.pending
      );


      const recommendedCard =
        cards.find(function (card) {

          const availableHandles =
            String(
              card.dataset.groupHandles ||
              ''
            )
              .split(',')
              .map(function (handle) {
                return normaliseHandle(
                  handle
                );
              })
              .filter(Boolean);


          return (
            preferredPlantType &&
            availableHandles.includes(
              preferredPlantType
            )
          );

        });


      cards.forEach(function (card) {

        card.classList.remove(
          'opt-card--active',
          'opt-card--recommended'
        );

      });


      if (recommendedCard) {

        recommendedCard.classList.add(
          'opt-card--recommended'
        );

        grid.prepend(
          recommendedCard
        );

      }

    });

  }


  /* ==================================================
     PLANT DRAWER
  ================================================== */

  function initialiseDrawer() {

    const drawer =
      document.getElementById(
        'product-tree-options'
      );

    if (!drawer) return;


    let lastTrigger = null;


    function closeDrawer() {

      drawer.classList.remove(
        'active',
        'is-open'
      );

      drawer.setAttribute(
        'aria-hidden',
        'true'
      );

      document.body.classList.remove(
        'plant-drawer-open'
      );


      document
        .querySelectorAll(
          '.js-plant-drawer-toggle'
        )
        .forEach(function (button) {

          button.setAttribute(
            'aria-expanded',
            'false'
          );

        });


      if (lastTrigger) {
        lastTrigger.focus();
      }

    }


    function openDrawer(trigger) {

      const selectedHandles =
        (
          trigger.dataset.groupHandles ||
          ''
        )
          .split(',')
          .map(function (value) {
            return value.trim();
          })
          .filter(Boolean);


      const showAllVariants =
        selectedHandles.length === 0;


      const titleText =
        trigger.dataset.drawerTitle ||
        'Choose your option';


      let visibleCount = 0;


      drawer
        .querySelectorAll(
          '[data-popup-variant-item]'
        )
        .forEach(function (item) {

          const shouldShow =
            showAllVariants ||
            selectedHandles.includes(
              item.dataset.filterHandle
            );


          item.hidden = !shouldShow;

          item.classList.toggle(
            'hidden',
            !shouldShow
          );


          if (shouldShow) {
            visibleCount += 1;
          }

        });


      const title =
        drawer.querySelector(
          '.js-plant-drawer-title'
        );


      if (title) {

        title.textContent =
          titleText +
          ' (' +
          visibleCount +
          ')';

      }


      document
        .querySelectorAll(
          '.js-plant-drawer-toggle'
        )
        .forEach(function (button) {

          button.setAttribute(
            'aria-expanded',
            button === trigger
              ? 'true'
              : 'false'
          );

        });


      lastTrigger = trigger;


      drawer.classList.add(
        'active',
        'is-open'
      );


      drawer.setAttribute(
        'aria-hidden',
        'false'
      );


      document.body.classList.add(
        'plant-drawer-open'
      );


      const closeButton =
        drawer.querySelector(
          '.js-close-plant-drawer'
        );


      if (closeButton) {
        closeButton.focus();
      }

    }


    document.addEventListener(
      'click',
      function (event) {

        const trigger =
          event.target.closest(
            '.js-plant-drawer-toggle, side-drawer-toggle[data-drawer="product-tree-options"]'
          );


        const closeButton =
          event.target.closest(
            '.js-close-plant-drawer, .js-back-to-product'
          );


        if (trigger) {

          event.preventDefault();

          openDrawer(trigger);

          return;
        }


        if (closeButton) {

          event.preventDefault();

          closeDrawer();

        }

      }
    );


    document.addEventListener(
      'keydown',
      function (event) {

        if (
          event.key === 'Escape' &&
          drawer.classList.contains(
            'is-open'
          )
        ) {

          closeDrawer();

        }

      }
    );

  }


  /* ==================================================
     INITIALISE MODULE
  ================================================== */

  function init() {

    initialiseJourneyTracking();

    initialiseRecommendation();

    initialiseDrawer();

  }


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  } else {

    init();

  }

})();